import Card from "@mui/material/Card";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";
import Grid from "@mui/material/Grid";
import ArrowCircleDownIcon from "@mui/icons-material/ArrowCircleDown";
import useParentsData from "Views/people/parents/data/parentsTableData";
import { useState, useEffect } from "react";
import ApnaTabs from "components/ApnaTabs/ApnaTabs";
import ArgonInput from "components/ArgonInput";
import DeleteDialog from "components/DeleteDialog/DeleteDialog";
import PaginationBar from "components/PaginationBar/PaginationBar";
import axios, { baseURL } from "utils/api";
import { toast } from "react-toastify";
import ArgonButton from "components/ArgonButton";
import CsvBulkUploadDialog from "components/CsvBulkUploadDialog";
import UploadFileIcon from "@mui/icons-material/UploadFile";

function Tables() {
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [parentToDelete, setParentToDelete] = useState(null);
  const [refreshData, setRefreshData] = useState(false);
  const [activeTab, setActiveTab] = useState("All");
  const [bulkUploadDialog, setBulkUploadDialog] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const handleDeleteClick = (parentId) => {
    setParentToDelete(parentId);
    setDeleteDialog(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setDeleteDialog(false);
    setParentToDelete(null);
  };

  // Confirm delete function
  const handleConfirmDelete = async () => {
    if (!parentToDelete) return;

    try {
      const response = await axios.put(`${baseURL}/parent/profile/${parentToDelete}`, {
        isDeleted: true,
      });

      if (response.data.success) {
        toast.success("Parent deleted successfully");
        setRefreshData(!refreshData); // Trigger refresh
      } else {
        toast.error(response.data.msg || "Failed to delete parent");
      }
    } catch (error) {
      console.error("Error deleting parent:", error);
      toast.error(error.response?.data?.msg || "Failed to delete parent");
    } finally {
      handleCloseDialog();
    }
  };

  const { columns, rows, counts, loading } = useParentsData(
    handleDeleteClick,
    refreshData,
    activeTab
  );

  // Pagination handlers
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(event.target.value);
    setCurrentPage(1); // Reset to first page when changing rows per page
  };

  // Search handler
  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Tab change handler
  const handleTabChange = (status) => {
    setActiveTab(status);
    setCurrentPage(1); // Reset to first page when changing tabs
    setSearchQuery(""); // Reset search when changing tabs
  };

  // Tab data with counts - Split into left and right groups
  const leftTabsData = [
    { label: "All", count: counts?.All || 0, color: "#2027e1ff", status: "All" },
    { label: "Active", count: counts?.ACTIVE || 0, color: "#10ec3cff", status: "ACTIVE" },
    { label: "Hold", count: counts?.HOLD || 0, color: "#ec3c10ff", status: "HOLD" },
    { label: "Completed", count: counts?.COMPLETED || 0, color: "#10a6ecff", status: "COMPLETED" },
    { label: "Cancelled", count: counts?.CANCELLED || 0, color: "#ff0000", status: "CANCELLED" },
    {
      label: "Discontinued",
      count: counts?.DISCONTINUED || 0,
      color: "#ff0000",
      status: "DISCONTINUED",
    },
  ];

  const rightTabsData = [
    { label: "Combined", count: counts?.COMBINED || 0, color: "#9c27b0", status: "COMBINED" },
    { label: "SME", count: counts?.SME || 0, color: "#ff9800", status: "SME" },
    { label: "Separate", count: counts?.SEPARATE || 0, color: "#00bcd4", status: "SEPARATE" },
  ];

  // Calculate paginated rows
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;

  // Filter rows based on search query
  const filteredRows = rows.filter((row) => {
    if (!searchQuery) return true;

    const searchLower = searchQuery.toLowerCase();
    const searchDigits = searchQuery.replace(/\D/g, "");

    // Search in parent name (profile column)
    const parentName = row.profile?.props?.name || "";
    if (String(parentName).toLowerCase().includes(searchLower)) return true;

    // Search in parent phone number
    const parentPhone = row.parentPhone || "";
    const parentPhoneDigits = String(parentPhone).replace(/\D/g, "");
    if (
      String(parentPhone).toLowerCase().includes(searchLower) ||
      (searchDigits && parentPhoneDigits.includes(searchDigits))
    ) {
      return true;
    }

    // Search in students (array of student names)
    const students = row.students?.props?.students || [];
    if (students.some((student) => String(student).toLowerCase().includes(searchLower)))
      return true;

    // Search in class name
    const className = row["class Name"]?.props?.children || "";
    if (String(className).toLowerCase().includes(searchLower)) return true;

    // Search in city
    const city = row.city?.props?.children || "";
    if (String(city).toLowerCase().includes(searchLower)) return true;

    // Search in area
    const area = row.area?.props?.children || "";
    if (String(area).toLowerCase().includes(searchLower)) return true;

    return false;
  });

  const paginatedRows = filteredRows.slice(startIndex, endIndex);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={3}>
              <ArgonBox display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                <ArgonTypography variant="h6" fontWeight="medium">
                  All Parents
                </ArgonTypography>
                <ArgonButton
                  variant="gradient"
                  color="info"
                  size="small"
                  onClick={() => setBulkUploadDialog(true)}
                  startIcon={<UploadFileIcon />}
                  sx={{
                    height: "38px",
                    px: 2,
                    borderRadius: "8px",
                    boxShadow: "0 8px 18px rgba(23, 193, 232, 0.28)",
                    textTransform: "none",
                    fontWeight: 700,
                    whiteSpace: "nowrap",
                  }}
                >
                  Bulk Upload
                </ArgonButton>
              </ArgonBox>

              {/* Tabs Section - Left and Right */}
              <ArgonBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                {/* Left Tabs */}
                <ArgonBox>
                  <ApnaTabs
                    tabsData={leftTabsData}
                    onTabChange={handleTabChange}
                    activeTab={activeTab}
                  />
                </ArgonBox>

                {/* Right Tabs */}
                <ArgonBox>
                  <ApnaTabs
                    tabsData={rightTabsData}
                    onTabChange={handleTabChange}
                    activeTab={activeTab}
                  />
                </ArgonBox>
              </ArgonBox>

              {/* Search Section */}
              <ArgonBox display="flex" alignItems="center" justifyContent="flex-end" mb={1}>
                <ArgonBox display="flex" alignItems="center" gap={1}>
                  <ArgonInput
                    placeholder="search parents..."
                    name="search"
                    size="small"
                    type="text"
                    variant="outlined"
                    value={searchQuery}
                    onChange={handleSearchChange}
                  />
                </ArgonBox>
              </ArgonBox>
            </ArgonBox>
            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {loading ? (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="h6" color="text" fontWeight="medium">
                    Fetching parents...
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="secondary">
                    Please wait while we load the latest data.
                  </ArgonTypography>
                </ArgonBox>
              ) : filteredRows.length > 0 ? (
                <Table columns={columns} rows={paginatedRows} />
              ) : (
                <ArgonBox textAlign="center" py={4}>
                  <ArgonTypography variant="h6" color="text" fontWeight="medium">
                    {searchQuery ? "No parents found matching your search" : "No parents found"}
                  </ArgonTypography>
                  <ArgonTypography variant="caption" color="secondary">
                    {searchQuery ? "Try adjusting your search terms." : "No parent data available."}
                  </ArgonTypography>
                </ArgonBox>
              )}
            </ArgonBox>

            {/* Pagination Controls */}
            {filteredRows.length > 0 && (
              <PaginationBar
                currentPage={currentPage}
                rowsPerPage={rowsPerPage}
                totalItems={filteredRows.length}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            )}
          </Card>
        </ArgonBox>
      </ArgonBox>
      <DeleteDialog
        open={deleteDialog}
        onClose={handleCloseDialog}
        onConfirm={handleConfirmDelete}
        title="Delete Parent"
        description="Are you sure you want to delete this parent? This action cannot be undone."
        confirmText="Delete Parent"
        cancelText="Cancel"
      />
      <CsvBulkUploadDialog
        open={bulkUploadDialog}
        onClose={() => setBulkUploadDialog(false)}
        title="Parent Bulk Upload"
        endpoint="/parent/bulk-upload/csv"
        description="Upload parent CSV using the parent bulk format. Each row can create one parent and up to two students."
        onSuccess={() => setRefreshData((value) => !value)}
      />
      <Footer />
    </DashboardLayout>
  );
}

export default Tables;

